import {
  getMinCheckoutChargeMessage,
  MIN_CHECKOUT_CHARGE_ERROR_CODE,
} from "./checkout";

const HEBREW_CHAR_PATTERN = /[\u0590-\u05FF]/;

export const CHECKOUT_ERROR_CATEGORY = {
  PAYMENT: "payment",
  PROFILE: "profile",
  ADDRESS: "address",
  CHECKOUT: "checkout",
  NETWORK: "network",
};

/**
 * PGW returns this message with a 404 when OMS has no OPEN cart for the
 * customer — typically because a previous checkout already succeeded and
 * closed the cart, while the browser still shows the stale local cart.
 * Kept as the single source of truth; stale-cart recovery matches on it.
 */
export const STALE_CART_ERROR_MESSAGE =
  "Failed to load cart. Please refresh the page and try again.";

/** Maps PGW checkout/payment errors (EN) to Hebrew customer-facing messages. */
export const CUSTOMER_PAYMENT_ERROR_HE = {
  [STALE_CART_ERROR_MESSAGE]:
    "אירעה תקלה בטעינת עגלת הקניות. אנא רענן את הדף ונסה שוב.",
  "Failed to initiate payment transaction. Please try again in a few moments.":
    "אירעה תקלה בפתיחת עסקת התשלום. אנא נסה שוב בעוד מספר רגעים.",
  "Payment failed. Please try again or use a different card.":
    "התשלום נכשל. אנא נסה שוב או השתמש בכרטיס אחר.",
  "Payment was processed but order creation failed. We attempted to cancel the charge. If you were charged, please contact support before trying again.":
    "התשלום בוצע אך יצירת ההזמנה נכשלה. ניסינו לבטל את החיוב. אם חויבת, אנא פנה לשירות הלקוחות לפני שתנסה שוב.",
  "Failed to create order. Please try again or contact support.":
    "יצירת ההזמנה נכשלה. אנא נסה שוב או פנה לשירות הלקוחות.",
  "An unexpected error occurred. Please try again.":
    "אירעה שגיאה בלתי צפויה. אנא נסה שוב.",
  "Failed to apply coupon. Please verify the coupon code and try again.":
    "החלת הקופון נכשלה. אנא וודא את קוד הקופון ונסה שוב.",
  "Payment service is currently unavailable. Please try again in a few minutes.":
    "שירות התשלום אינו זמין כרגע. אנא נסה שוב בעוד מספר דקות.",
  "An unexpected error occurred while processing payment. Please try again.":
    "אירעה שגיאה בלתי צפויה בעת עיבוד התשלום. אנא נסה שוב.",
};

const PROFILE_ERROR_PATTERNS = [
  {
    pattern: /phone|טלפון/i,
    message: "מספר הטלפון אינו תקין. אנא עדכן את הפרטים האישיים ונסה שוב.",
  },
  {
    pattern: /firstName|שם פרטי/i,
    message: "שם פרטי אינו תקין. אנא עדכן את הפרטים האישיים ונסה שוב.",
  },
  {
    pattern: /lastName|שם משפחה/i,
    message: "שם משפחה אינו תקין. אנא עדכן את הפרטים האישיים ונסה שוב.",
  },
];

const ADDRESS_ERROR_PATTERNS = [
  {
    pattern: /addrCity|city|עיר/i,
    message: "יש למלא עיר תקינה בכתובת המשלוח.",
  },
  {
    pattern: /addrStreet|street|רחוב/i,
    message: "יש למלא רחוב תקין בכתובת המשלוח.",
  },
  {
    pattern: /addrHouse|house|בניין|מספר בית/i,
    message: "מספר הבית אינו תקין. אנא עדכן את כתובת המשלוח.",
  },
  {
    pattern: /addrApartment|apartment|דירה/i,
    message: "מספר הדירה אינו תקין. אנא עדכן את כתובת המשלוח.",
  },
  {
    pattern: /addrFloor|floor|קומה/i,
    message: "מספר הקומה אינו תקין. אנא עדכן את כתובת המשלוח.",
  },
  {
    pattern: /addrZipCode|zip|מיקוד/i,
    message: "מיקוד אינו תקין. אנא עדכן את כתובת המשלוח.",
  },
  {
    pattern: /addrOrderRecipientName|שם מקבל|מקבל ההזמנה/i,
    message: "שם מקבל ההזמנה אינו תקין. אנא עדכן את כתובת המשלוח.",
  },
  {
    pattern: /address|כתובת/i,
    message: "כתובת המשלוח אינה תקינה. אנא עדכן את הפרטים ונסה שוב.",
  },
];

const PAYMENT_ERROR_PATTERNS = [
  /payment/i,
  /card/i,
  /credit/i,
  /transaction/i,
  /charge/i,
  /כרטיס/i,
  /תשלום/i,
];

function matchPatternList(message, patterns) {
  for (const entry of patterns) {
    if (entry.pattern.test(message)) {
      return entry.message;
    }
  }
  return null;
}

function inferCategoryFromHebrewMessage(message) {
  if (/טלפון|שם פרטי|שם משפחה/.test(message)) {
    return CHECKOUT_ERROR_CATEGORY.PROFILE;
  }
  if (/כתובת|רחוב|עיר|בניין|דירה|קומה|מיקוד|מקבל/.test(message)) {
    return CHECKOUT_ERROR_CATEGORY.ADDRESS;
  }
  if (/כרטיס|תשלום/.test(message)) {
    return CHECKOUT_ERROR_CATEGORY.PAYMENT;
  }
  return CHECKOUT_ERROR_CATEGORY.CHECKOUT;
}

export function getCheckoutErrorTitle(
  category,
  message,
  minCheckoutChargeMessage,
) {
  if (message === minCheckoutChargeMessage) {
    return "לא ניתן להשלים את ההזמנה";
  }
  switch (category) {
    case CHECKOUT_ERROR_CATEGORY.PROFILE:
      return "יש לתקן את הפרטים האישיים";
    case CHECKOUT_ERROR_CATEGORY.ADDRESS:
      return "יש לתקן את כתובת המשלוח";
    case CHECKOUT_ERROR_CATEGORY.NETWORK:
      return "תקלת חיבור";
    case CHECKOUT_ERROR_CATEGORY.PAYMENT:
      return "התשלום נכשל";
    default:
      return "לא ניתן להשלים את ההזמנה";
  }
}

export function getCheckoutErrorFallback(category) {
  switch (category) {
    case CHECKOUT_ERROR_CATEGORY.PROFILE:
      return "אנא בדוק את הפרטים האישיים ונסה שוב";
    case CHECKOUT_ERROR_CATEGORY.ADDRESS:
      return "אנא בדוק את כתובת המשלוח ונסה שוב";
    case CHECKOUT_ERROR_CATEGORY.NETWORK:
      return "תקלת רשת. אנא בדוק את החיבור לאינטרנט ונסה שוב.";
    case CHECKOUT_ERROR_CATEGORY.PAYMENT:
      return "אנא בדוק את פרטי הכרטיס ונסה שנית";
    default:
      return "אירעה שגיאה. אנא בדוק את הפרטים ונסה שוב.";
  }
}

/**
 * Classifies checkout/payment API errors and returns a customer-facing message + category.
 */
export function resolveCheckoutError(
  rawMessage,
  { isNetworkError = false, minCheckoutChargeAmount } = {},
) {
  if (isNetworkError) {
    return {
      category: CHECKOUT_ERROR_CATEGORY.NETWORK,
      message: getCheckoutErrorFallback(CHECKOUT_ERROR_CATEGORY.NETWORK),
    };
  }

  const normalized = String(rawMessage ?? "").trim();
  if (!normalized) {
    return {
      category: CHECKOUT_ERROR_CATEGORY.PAYMENT,
      message: getCheckoutErrorFallback(CHECKOUT_ERROR_CATEGORY.PAYMENT),
    };
  }

  if (normalized === MIN_CHECKOUT_CHARGE_ERROR_CODE) {
    return {
      category: CHECKOUT_ERROR_CATEGORY.CHECKOUT,
      message: getMinCheckoutChargeMessage(minCheckoutChargeAmount),
    };
  }

  if (CUSTOMER_PAYMENT_ERROR_HE[normalized]) {
    return {
      category: CHECKOUT_ERROR_CATEGORY.PAYMENT,
      message: CUSTOMER_PAYMENT_ERROR_HE[normalized],
    };
  }

  const profileMessage = matchPatternList(normalized, PROFILE_ERROR_PATTERNS);
  if (profileMessage) {
    return {
      category: CHECKOUT_ERROR_CATEGORY.PROFILE,
      message: profileMessage,
    };
  }

  const addressMessage = matchPatternList(normalized, ADDRESS_ERROR_PATTERNS);
  if (addressMessage) {
    return {
      category: CHECKOUT_ERROR_CATEGORY.ADDRESS,
      message: addressMessage,
    };
  }

  if (HEBREW_CHAR_PATTERN.test(normalized)) {
    return {
      category: inferCategoryFromHebrewMessage(normalized),
      message: normalized,
    };
  }

  if (PAYMENT_ERROR_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return {
      category: CHECKOUT_ERROR_CATEGORY.PAYMENT,
      message: getCheckoutErrorFallback(CHECKOUT_ERROR_CATEGORY.PAYMENT),
    };
  }

  return {
    category: CHECKOUT_ERROR_CATEGORY.CHECKOUT,
    message: "לא ניתן להשלים את ההזמנה. אנא בדוק את הפרטים ונסה שוב.",
  };
}
